import type { AiDifficulty } from "../game/ai";
import { TIME_CONTROL_PRESETS } from "../game/clock";

const STORAGE_KEY = "dkg-chess:menu-settings:v3";
const MAX_NAME_LENGTH = 18;

export interface PlayerSetting {
  name: string;
  isAI: boolean;
  difficulty: AiDifficulty;
}

export interface MenuSettings {
  white: PlayerSetting;
  black: PlayerSetting;
  /** Id d'un `TimeControlPreset` (cf. game/clock.ts) ; "unlimited" = pas de chrono. */
  timeControlId: string;
  /** Cases cibles (et prises, en rouge) affichees en selectionnant une piece. */
  showMoveHints: boolean;
  /** Le Taverneu commente-t-il la partie (bulle en jeu) ? N'affecte pas les tutoriels/exercices. */
  taverneuCommentary: boolean;
}

export function defaultMenuSettings(): MenuSettings {
  return {
    white: { name: "Blancs", isAI: false, difficulty: "normal" },
    black: { name: "Ordinateur", isAI: true, difficulty: "normal" },
    timeControlId: "unlimited",
    showMoveHints: true,
    taverneuCommentary: true,
  };
}

function sanitizeName(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim().slice(0, MAX_NAME_LENGTH);
  return trimmed || fallback;
}

function sanitizeDifficulty(value: unknown, fallback: AiDifficulty): AiDifficulty {
  return value === "easy" || value === "normal" || value === "hard" ? value : fallback;
}

function sanitizeTimeControlId(value: unknown, fallback: string): string {
  return typeof value === "string" && TIME_CONTROL_PRESETS.some((preset) => preset.id === value) ? value : fallback;
}

function sanitizeBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function sanitizePlayer(value: unknown, fallback: PlayerSetting): PlayerSetting {
  if (!value || typeof value !== "object") return fallback;
  const v = value as Partial<PlayerSetting>;
  return {
    name: sanitizeName(v.name, fallback.name),
    isAI: typeof v.isAI === "boolean" ? v.isAI : fallback.isAI,
    difficulty: sanitizeDifficulty(v.difficulty, fallback.difficulty),
  };
}

/** Relit les reglages sauvegardes ; retombe sur les valeurs par defaut si absents/corrompus. */
export function loadMenuSettings(): MenuSettings {
  const fallback = defaultMenuSettings();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<MenuSettings> | null;
    if (!parsed || typeof parsed !== "object") return fallback;
    return {
      white: sanitizePlayer(parsed.white, fallback.white),
      black: sanitizePlayer(parsed.black, fallback.black),
      timeControlId: sanitizeTimeControlId(parsed.timeControlId, fallback.timeControlId),
      showMoveHints: sanitizeBoolean(parsed.showMoveHints, fallback.showMoveHints),
      taverneuCommentary: sanitizeBoolean(parsed.taverneuCommentary, fallback.taverneuCommentary),
    };
  } catch {
    return fallback;
  }
}

export function saveMenuSettings(settings: MenuSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Stockage indisponible (navigation privee, quota...) : tant pis, pas bloquant.
  }
}
